import store from '@/store'
import { generateStartEndPointsFromGeoJSON, generateSegmentLabelGeoJSON, lavelUpdate } from '@/js/pyramid'
import {
  saveDrowFeatures,
  featureCollectionAdd,
  markerAddAndRemove,
  removeThumbnailMarkerByKey,
  featuresDelete,
  pushMyDrawUndoAction,
  updateSingleMarker,
} from '@/js/downLoad'
import { getSourceData, clearSelectedFeature } from '@/js/draw'

/**
 * マーカー再作成が必要なプロパティのリスト
 * これらのプロパティが変更された場合のみmarkerAddAndRemove(true)を呼ぶ
 */
const MARKER_AFFECTING_PROPERTIES = new Set([
  'label', // ラベルテキスト（lavelUpdateで特別処理）
  'labelType', // ラベルタイプ（通常/吹き出し）
  'balloonDirection', // 吹き出しの方向（top/right/bottom/left）
  'text-color', // テキスト色
  'point-color', // ポイント色
  'color', // 共通色プロパティ
  'text-size', // テキストサイズ
  'offsetValue', // オフセット位置
])

/**
 * プロパティ変更がマーカー再作成を必要とするか判定
 */
function needsMarkerRecreation(propertyName) {
  return MARKER_AFFECTING_PROPERTIES.has(propertyName)
}

// サーバー保存の連打を防ぐための簡易デバウンス
let saveTimer = null

/**
 * スケジュールされた保存をキャンセル（Undo/Redo時に使用）
 */
export function cancelScheduledSave() {
  if (saveTimer) {
    clearTimeout(saveTimer)
    saveTimer = null
  }
}

function scheduleSave(geojson) {
  // 編集・移動モード中は確定までサーバー保存しない
  if (store.state.editEnabled) {
    console.info('[edit-mode] skip auto-save (scheduleSave) while editEnabled=true')
    return
  }
  // ローカル編集時刻だけを更新（ポーリングの待機判断用）
  store.state._lastLocalEditAt = Date.now()
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(async () => {
    saveTimer = null
    store.state._lastLocalEditAt = Date.now()
    await saveDrowFeatures(geojson.features)
  }, 250)
}

/**
 * Feature の複数プロパティを一度に更新（保存は1回だけ）
 */
export function updateFeatureProperties(featureId, properties) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource('click-circle-source')
  if (!source) return

  const geojson = getSourceData(source)
  if (!geojson || !geojson.features) return

  const feature = geojson.features.find((f) => f.properties?.id === featureId)
  if (!feature) return

  // マイドロー用：更新前の状態を保存（Undo用）
  let prevState = null
  if (store.state.isUsingServerGeojson && featureId && featureId !== 'config') {
    prevState = JSON.parse(JSON.stringify(feature))
  }

  // 複数のプロパティを一度に更新
  Object.entries(properties).forEach(([key, value]) => {
    feature.properties[key] = value
  })

  // マイドロー用Undoスタックに追加
  if (prevState) {
    pushMyDrawUndoAction('modify', String(featureId), prevState)
  }

  // ソースを更新
  source.setData(geojson)

  // populationが更新された場合、レイヤーを最前面に移動
  if ('population' in properties) {
    // 人口ラベルレイヤーを最前面に移動
    if (map.getLayer('click-circle-polygon-population-layer')) {
      map.moveLayer('click-circle-polygon-population-layer')
    }
    if (map.triggerRepaint) {
      map.triggerRepaint()
    }
  }

  // 関連データを更新（LineString関連のプロパティ変更時のみ）
  // 矢印や距離計測に影響するプロパティの場合のみ更新
  const propertyNames = Object.keys(properties)
  const lineAffectingProps = ['color', 'arrow-type', 'labelType', 'calc', 'label']
  const needsLineUpdate = propertyNames.some((prop) => lineAffectingProps.includes(prop))

  if (needsLineUpdate && feature.geometry.type === 'LineString') {
    // LineStringの矢印・ラベル関連のみ更新
    generateStartEndPointsFromGeoJSON(geojson)
    generateSegmentLabelGeoJSON(geojson)
  }

  // 永続化（1回だけ）
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  scheduleSave(geojson)

  // featureCollectionを更新（マイドローモードでもポーリングとの整合性のため必要）
  featureCollectionAdd()

  // マーカー削除が必要な変更時は座標キーで既存マーカーを除去
  const removeMarkerIfNeeded = (propExists) => {
    if (!propExists) return
    const key = feature.geometry?.coordinates?.join()
    if (key) removeThumbnailMarkerByKey(key)
  }
  removeMarkerIfNeeded(properties.labelType !== undefined)
  removeMarkerIfNeeded(properties.balloonDirection !== undefined)

  // マーカー再作成が必要なプロパティが含まれているかチェック
  const needsMarker = propertyNames.some((prop) => needsMarkerRecreation(prop))

  // ラベル変更時もマーカーを再作成して即時反映（該当マーカーのみ更新）
  if (needsMarker || properties.label !== undefined) {
    const key = feature.geometry?.coordinates?.join()
    if (key) removeThumbnailMarkerByKey(key)
    updateSingleMarker(featureId)
  }

  // パーマリンク更新
  store.state.updatePermalinkFire = !store.state.updatePermalinkFire

  // 現在のFeatureも更新
  const currentFeature = store.state.currentDrawFeature
  if (currentFeature && currentFeature.properties.id === featureId) {
    store.commit('setCurrentDrawFeature', { ...feature })
  }
}

/**
 * Feature のプロパティを更新
 */
export function updateFeatureProperty(featureId, propertyName, value) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource('click-circle-source')
  if (!source) return

  const geojson = getSourceData(source)
  if (!geojson || !geojson.features) return

  const feature = geojson.features.find((f) => f.properties?.id === featureId)
  if (!feature) return

  // マイドロー用：更新前の状態を保存（Undo用）
  let prevState = null
  if (store.state.isUsingServerGeojson && featureId && featureId !== 'config') {
    prevState = JSON.parse(JSON.stringify(feature))
  }

  // プロパティを更新
  feature.properties[propertyName] = value
  // ローカル編集のタイムスタンプを即更新
  store.state._lastLocalEditAt = Date.now()

  // マイドロー用Undoスタックに追加
  if (prevState) {
    pushMyDrawUndoAction('modify', String(featureId), prevState)
  }

  // ソースを更新
  source.setData(geojson)

  // 関連データを更新
  generateStartEndPointsFromGeoJSON(geojson)
  generateSegmentLabelGeoJSON(geojson)

  // 永続化
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  scheduleSave(geojson)

  // featureCollectionを更新（マイドローモードでもポーリングとの整合性のため必要）
  featureCollectionAdd()

  // マーカー再作成が必要なプロパティ or ラベル変更時はマーカーを再作成（該当マーカーのみ更新）
  const needsMarker = needsMarkerRecreation(propertyName) || propertyName === 'label'
  if (needsMarker) {
    const key = feature.geometry?.coordinates?.join()
    if (key) removeThumbnailMarkerByKey(key)
    updateSingleMarker(featureId)
  }

  // パーマリンク更新
  store.state.updatePermalinkFire = !store.state.updatePermalinkFire

  // 現在のFeatureも更新（FloatingWindow用）
  const currentFeature = store.state.currentDrawFeature
  if (currentFeature && currentFeature.properties.id === featureId) {
    store.commit('setCurrentDrawFeature', { ...feature })
  }

  // AIマーカーのラベル変更を検知してstoreに通知
  if (propertyName === 'label' && feature.properties.aiGenerated) {
    const markerNumber = feature.properties.aiMarkerNumber
    store.state.aiMarkerLabelChanged = {
      markerNumber,
      newLabel: value,
      timestamp: Date.now()
    }
  }
}

/**
 * Feature を最前面（配列末尾）に移動
 */
export function moveFeatureToFront(featureId) {
  reorderFeature(featureId, 'front')
}

/**
 * Feature を最背面（配列先頭）に移動
 */
export function moveFeatureToBack(featureId) {
  reorderFeature(featureId, 'back')
}

/**
 * Feature の重なり順を変更（共通処理）
 */
function reorderFeature(featureId, direction) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource('click-circle-source')
  if (!source) return

  const geojson = getSourceData(source)
  if (!geojson || !geojson.features) return

  // 対象feature（Circleの場合は'-point'ペアも含む）
  const targetIds = new Set([String(featureId)])
  const pointId = featureId + '-point'
  if (geojson.features.some(f => f.properties?.id === pointId)) {
    targetIds.add(pointId)
  }

  const targets = geojson.features.filter(f => targetIds.has(String(f.properties?.id)))
  const others = geojson.features.filter(f => !targetIds.has(String(f.properties?.id)))

  if (targets.length === 0) return

  // 並び替え
  geojson.features = direction === 'front'
    ? [...others, ...targets]
    : [...targets, ...others]

  // 全featureにz_orderを振る（配列順 = 描画順）
  geojson.features.forEach((f, i) => {
    if (f.properties) {
      f.properties.z_order = i
    }
  })

  // ローカル編集のタイムスタンプを即更新
  store.state._lastLocalEditAt = Date.now()

  // ソースを更新
  source.setData(geojson)

  // 関連データを更新
  generateStartEndPointsFromGeoJSON(geojson)
  generateSegmentLabelGeoJSON(geojson)

  // 永続化
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  scheduleSave(geojson)

  // featureCollectionを更新
  featureCollectionAdd()

  // パーマリンク更新
  store.state.updatePermalinkFire = !store.state.updatePermalinkFire
}

/**
 * Feature を削除
 */
export function deleteFeature(featureId) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource('click-circle-source')
  if (!source) return

  const geojson = getSourceData(source)
  if (!geojson || !geojson.features) return

  // マイドロー用：削除前の状態を保存（Undo用）
  let deletedFeature = null
  let deletedCirclePoint = null
  const circlePointId = featureId + '-point'
  if (store.state.isUsingServerGeojson) {
    deletedFeature = geojson.features.find((f) => f.properties?.id === featureId)
    deletedCirclePoint = geojson.features.find((f) => f.properties?.id === circlePointId)
    if (deletedFeature) {
      deletedFeature = JSON.parse(JSON.stringify(deletedFeature))
    }
    if (deletedCirclePoint) {
      deletedCirclePoint = JSON.parse(JSON.stringify(deletedCirclePoint))
    }
  }

  // 該当Featureを削除
  // 円の場合はペアになっている中心点（id-point）も一緒に削除
  geojson.features = geojson.features.filter((f) => {
    const id = f.properties?.id
    return id !== featureId && id !== circlePointId
  })

  // ソースを更新
  source.setData(geojson)

  // 関連データを更新
  generateStartEndPointsFromGeoJSON(geojson)
  generateSegmentLabelGeoJSON(geojson)

  // 永続化／サーバ反映
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  const idsToDelete = [featureId] // DB側のfeature_idは中心点を持たないので基点のみ送る
  if (store.state.isUsingServerGeojson) {
    // マイドロー用Undoスタックに追加（削除前の状態を保存）
    if (deletedFeature) {
      pushMyDrawUndoAction('delete', featureId, deletedFeature)
    }
    if (deletedCirclePoint) {
      pushMyDrawUndoAction('delete', circlePointId, deletedCirclePoint)
    }
    // サーバーに削除を伝える
    featuresDelete(idsToDelete)
  } else {
    saveDrowFeatures(geojson.features)
    featureCollectionAdd()
    markerAddAndRemove(true)
  }

  // パーマリンク更新
  store.state.updatePermalinkFire = !store.state.updatePermalinkFire

  // 選択をクリア（シアン色の枠を消す）
  clearSelectedFeature(map)
}

/**
 * 指定したFeature以外を削除
 */
export function deleteOtherFeatures(featureId) {
  const map = store.state.map01
  if (!map) return

  const source = map.getSource('click-circle-source')
  if (!source) return

  const geojson = getSourceData(source)
  if (!geojson || !geojson.features) return

  const deletedIds = []
  // 該当Feature以外を削除（configは保持）
  geojson.features = geojson.features.filter((f) => {
    const fid = f.properties?.id
    if (fid === 'config') return true
    const keep = fid === featureId
    if (!keep && fid) deletedIds.push(fid)
    return keep
  })

  // ソースを更新
  source.setData(geojson)

  // 関連データを更新
  generateStartEndPointsFromGeoJSON(geojson)
  generateSegmentLabelGeoJSON(geojson)

  // 永続化／サーバ反映
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  if (store.state.isUsingServerGeojson) {
    if (deletedIds.length) {
      featuresDelete(deletedIds)
    }
  } else {
    saveDrowFeatures(geojson.features)
    featureCollectionAdd()
    markerAddAndRemove(true)
  }

  // パーマリンク更新
  store.state.updatePermalinkFire = !store.state.updatePermalinkFire
}
